import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import * as PO from '@radix-ui/react-popover';
import { ChevronDown, FilePlus2, MoreHorizontal, Save, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { ListResult, ScreenDef } from '@/lib/types';
import { Spinner, ErrorBox } from '@/ui/Spinner';
import { DataGrid } from '@/grid/DataGrid';
import { LookupDialog } from '@/grid/LookupDialog';
import { confirmDialog } from '@/ui/confirm';
import { formatDate, parseDateInput, parseNumberInput, formatTime } from '@/lib/format';
import { useTabId, useWorkspace } from '@/shell/workspace';
import { ReportMenu } from '@/reports/ReportMenu';
import { cn } from '@/lib/cn';
import { notifyDataChanged } from '@/grid/events';
import { DateInput } from '@/ui/DateInput';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { buildRowSchema, firstIssue } from '@/lib/metaSchema';

interface FieldMeta {
  field: string; caption: string; editor: string | null; readonly: boolean;
  lookup: { display: string[] } | null; combo: (string | { deger: unknown; metin: string })[] | null; picker: { title: string } | null;
  column: { type: string; maxLength: number | null; nullable: boolean } | null;
  required: { mesaj: string; sifirBos: boolean } | null;
}
interface FormData {
  title: string; table: string; key: string; titleField: string | null;
  tabs: { title: string; fields: FieldMeta[] }[];
  details: { index: number; title: string; table: string | null; fk: [string, string]; columns: { alan: string; baslik?: string; gorunur?: boolean }[] }[];
  row: Record<string, any>; rights: ScreenDef['rights']; deleteConfirm2: string | null;
}

const NUM = new Set(['int', 'bigint', 'smallint', 'tinyint', 'float', 'real', 'decimal', 'numeric', 'money', 'smallmoney']);
const DATE = new Set(['datetime', 'smalldatetime', 'date', 'datetime2']);

/**
 * Genel kayıt formu (Delphi Unit41_2 / Unit22_1 / Unit18_1 / Unit2_1 ... karşılığı).
 * Alanlar, sekmeler, lookup'lar, seçim listeleri ve kurallar Web_Screen.AYAR.kayitFormu tanımından gelir.
 * Kısayollar: F2 kaydet, Esc kapat.
 */
export function KayitFormu({ screen, id: initialId }: { screen: ScreenDef; id?: number | null }) {
  const qc = useQueryClient();
  const ws = useWorkspace();
  const tabId = useTabId();
  const [id, setId] = useState<number | null>(initialId ?? null);
  const [saving, setSaving] = useState(false);
  const [errField, setErrField] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['kayit', screen.tag, id ?? 0], queryFn: () => api<FormData>(`/screens/${screen.tag}/kayit/${id ?? 0}`), staleTime: 0 });
  // form durumu React Hook Form'da; doğrulama metadata'dan üretilen Zod şemasıyla (kolon tipi/uzunluğu + zorunlu alan kuralları)
  const schema = useMemo(() => buildRowSchema(q.data ? q.data.tabs.flatMap((t) => t.fields) : []), [q.data]);
  const form = useForm<Record<string, any>>({ resolver: zodResolver(schema), mode: 'onSubmit' });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (q.data) { form.reset({ ...q.data.row }); setLoaded(true); }
  }, [q.data]);
  const row = loaded ? form.watch() : null;
  const dirty = form.formState.isDirty;

  const set = (p: Record<string, any>) => { for (const [k, v] of Object.entries(p)) form.setValue(k, v, { shouldDirty: true }); };

  const save = () => form.handleSubmit(submit, (errors) => {
    const e = firstIssue(errors);
    if (e) { setErrField(e.field); toast.error(e.message); }
  })();

  const submit = async (row: Record<string, any>) => {
    setSaving(true);
    setErrField(null);
    try {
      const r = await api<{ id: number }>(`/screens/${screen.tag}/kayit`, { body: { id, row } });
      toast.success('Kaydedildi');
      const newId = Number(r.id);
      form.reset(row);
      notifyDataChanged(screen.tag);
      if (!id) {
        setId(newId);
        const t = q.data?.titleField ? row[q.data.titleField] : newId;
        ws.rename(tabId, `${q.data?.title ?? screen.title} · ${t ?? newId}`);
      } else qc.invalidateQueries({ queryKey: ['kayit', screen.tag, id] });
    } catch (e: any) {
      toast.error(e.message);
      if (e.details?.field) setErrField(e.details.field);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!id) return;
    if (!(await confirmDialog('Silmek İstediğinizden Eminmisiniz ?', { danger: true, ok: 'Sil' }))) return;
    if (q.data?.deleteConfirm2 && !(await confirmDialog(q.data.deleteConfirm2, { danger: true, ok: 'Evet, sil' }))) return;
    try {
      await api(`/screens/${screen.tag}/kayit/${id}/delete`, { body: { row } });
      toast.success('Silindi');
      notifyDataChanged(screen.tag);
      ws.close(tabId);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const close = async () => {
    if (dirty && !(await confirmDialog('Değişiklikler kaydedilmedi. Kapatılsın mı?', { ok: 'Kapat' }))) return;
    ws.close(tabId);
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (!document.getElementById(`kf-${tabId}`)?.offsetParent) return;
      if (e.key === 'F2') { e.preventDefault(); save(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  if (q.isLoading || !row) return q.error ? <ErrorBox error={q.error} /> : <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data!;
  const canSave = id ? d.rights.update : d.rights.insert;

  return (
    <div id={`kf-${tabId}`} className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 p-2" onKeyDown={(e) => { if (e.key === 'Escape') close(); }}>
      <div className="panel flex flex-wrap items-center gap-1.5 rounded px-2 py-1.5">
        <button className="btn btn-primary" disabled={!canSave || saving} onClick={save}><Save size={14} /> Kaydet (F2)</button>
        <button className="btn" disabled={!d.rights.insert} onClick={() => ws.open({ kind: 'custom', component: 'KayitFormu', title: `${d.title} · Yeni`, props: { screen, id: null } }, { newInstance: true })}><FilePlus2 size={14} /> Yeni</button>
        <button className="btn btn-danger" disabled={!id || !d.rights.delete} onClick={remove}><Trash2 size={14} /> Sil</button>
        <button className="btn" onClick={close}><X size={14} /> Kapat</button>
        <span className="ml-2 text-xs muted">{id ? `Kayıt No: ${id}` : 'Yeni Kayıt'}{dirty ? ' · kaydedilmedi' : ''}</span>
        <div className="ml-auto"><ReportMenu reports={screen.dizayn?.reports ?? screen.reports ?? []} row={id ? { ...row, ID: id } : null} /></div>
      </div>
      <FormTabs data={d} row={row} set={set} screen={screen} errField={errField} />
      {id && d.details.length > 0 && <Details screen={screen} id={id} details={d.details} />}
      {!id && d.details.length > 0 && <div className="panel rounded p-3 text-center text-xs muted">Detay bilgileri kayıt kaydedildikten sonra girilebilir.</div>}
    </div>
  );
}

function FormTabs({ data, row, set, screen, errField }: { data: FormData; row: Record<string, any>; set: (p: Record<string, any>) => void; screen: ScreenDef; errField: string | null }) {
  const [active, setActive] = useState(data.tabs[0]?.title ?? '');
  // doğrulama hatasında alanın bulunduğu sekmeye geç
  useEffect(() => {
    if (!errField) return;
    const t = data.tabs.find((x) => x.fields.some((f) => f.field === errField));
    if (t) setActive(t.title);
  }, [errField]);
  const body = (fields: FieldMeta[]) => (
    <div className="grid gap-x-4 gap-y-2 p-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
      {fields.map((f) => (
        <label key={f.field} className={cn('flex flex-col gap-0.5', (f.editor === 'memo' || f.column?.type === 'text') && 'col-span-full')}>
          <span className={cn('text-[11.5px]', errField === f.field ? 'font-semibold text-red-600' : 'muted')}>{humanize(f.caption, f.field)}</span>
          <FieldInput f={f} value={row[f.field]} row={row} onChange={set} tag={screen.tag} />
        </label>
      ))}
    </div>
  );
  if (data.tabs.length <= 1) return <div className="panel rounded">{data.tabs[0] && body(data.tabs[0].fields)}</div>;
  return (
    <Tabs.Root value={active} onValueChange={setActive} className="panel shrink-0 rounded">
      <Tabs.List className="flex gap-0.5 border-b b-border px-2 pt-1.5">
        {data.tabs.map((t) => (
          <Tabs.Trigger key={t.title} value={t.title} className="rounded-t px-3 py-1.5 text-[12.5px] muted data-[state=active]:bg-[var(--grid-header)] data-[state=active]:font-medium data-[state=active]:text-[var(--text)]">{t.title}</Tabs.Trigger>
        ))}
      </Tabs.List>
      {data.tabs.map((t) => <Tabs.Content key={t.title} value={t.title}>{body(t.fields)}</Tabs.Content>)}
    </Tabs.Root>
  );
}

function FieldInput({ f, value, row, onChange, tag }: { f: FieldMeta; value: any; row: Record<string, any>; onChange: (p: Record<string, any>) => void; tag: number }) {
  const [pick, setPick] = useState(false);
  const type = f.column?.type ?? '';
  const ed = f.editor ?? (type === 'bit' ? 'check' : NUM.has(type) ? 'number' : DATE.has(type) ? 'date' : type === 'text' || type === 'ntext' ? 'memo' : 'text');
  const ro = f.readonly;
  if (f.lookup) return <LookupSelect tag={tag} f={f} value={value} disabled={ro} onChange={(v) => onChange({ [f.field]: v })} />;
  if (ed === 'check') return <div className="flex h-7 items-center"><input type="checkbox" disabled={ro} checked={!!value} onChange={(e) => onChange({ [f.field]: e.target.checked })} /></div>;
  if (ed === 'memo') return <textarea className="inp !h-20 py-1" readOnly={ro} value={value ?? ''} onChange={(e) => onChange({ [f.field]: e.target.value })} />;
  if (ed === 'date') {
    return <DateInput value={value} readOnly={ro} onChange={(v) => onChange({ [f.field]: v })} />;
  }
  if (ed === 'time') {
    return <input type="time" step={1} className="inp" readOnly={ro} value={value ? formatTime(value) : ''} onChange={(e) => onChange({ [f.field]: e.target.value ? `1899-12-30T${e.target.value.length === 5 ? e.target.value + ':00' : e.target.value}` : null })} />;
  }
  if (ed === 'number') return <NumberField value={value} readOnly={ro} onChange={(v) => onChange({ [f.field]: v })} />;
  if (ed === 'combo' && f.combo) {
    const objs = f.combo.some((c) => typeof c === 'object');
    if (objs) {
      return (
        <select className="inp" disabled={ro} value={value == null ? '' : String(value)} onChange={(e) => {
          const it = (f.combo as { deger: unknown; metin: string }[]).find((x) => String(x.deger) === e.target.value);
          onChange({ [f.field]: it ? it.deger : null });
        }}>
          <option value="" />
          {(f.combo as { deger: unknown; metin: string }[]).map((c) => <option key={String(c.deger)} value={String(c.deger)}>{c.metin}</option>)}
        </select>
      );
    }
    const id = `dl-${tag}-${f.field}`;
    return <>
      <input className="inp" list={id} readOnly={ro} value={value ?? ''} maxLength={f.column?.maxLength ?? undefined} onChange={(e) => onChange({ [f.field]: e.target.value })} />
      <datalist id={id}>{(f.combo as string[]).map((c) => <option key={c} value={c} />)}</datalist>
    </>;
  }
  return (
    <div className="flex gap-1">
      <input className="inp" readOnly={ro} value={value ?? ''} maxLength={f.column?.maxLength ?? undefined} onChange={(e) => onChange({ [f.field]: e.target.value })} />
      {f.picker && !ro && <button type="button" className="btn !px-2" title="Listeden seç" onClick={() => setPick(true)}><MoreHorizontal size={14} /></button>}
      {pick && f.picker && (
        <LookupDialog open row={row} onClose={() => setPick(false)}
          spec={{ title: f.picker.title, fetch: (q) => api<ListResult>(`/screens/${tag}/kayit/secim/${f.field}`, { body: { query: q } }), apply: (p) => ({ [f.field]: p[f.field] ?? Object.values(p)[0] }) }}
          onPick={onChange} />
      )}
    </div>
  );
}

function NumberField({ value, readOnly, onChange }: { value: any; readOnly: boolean; onChange: (v: number | null) => void }) {
  const [t, setT] = useState(value == null ? '' : String(value).replace('.', ','));
  useEffect(() => setT(value == null ? '' : String(value).replace('.', ',')), [value]);
  return <input className="inp text-right" readOnly={readOnly} value={t} onChange={(e) => setT(e.target.value)} onBlur={() => {
    const n = parseNumberInput(t);
    if (t.trim() && n == null) { toast.error('Geçerli bir sayı giriniz'); return; }
    onChange(n);
  }} />;
}

/** Delphi TcxLookupComboBox karşılığı: aranabilir açılır liste (gösterim kolonları birleşik) */
function LookupSelect({ tag, f, value, onChange, disabled }: { tag: number; f: FieldMeta; value: any; onChange: (v: any) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const items = useQuery({
    queryKey: ['kayit-lookup', tag, f.field],
    queryFn: () => api<{ items: { value: any; label: string }[] }>(`/screens/${tag}/kayit/lookup/${encodeURIComponent(f.field)}`, { method: 'POST' }),
    staleTime: 5 * 60_000,
  });
  const current = items.data?.items.find((i) => String(i.value) === String(value));
  const filtered = useMemo(() => {
    const s = q.toLocaleLowerCase('tr-TR');
    return (items.data?.items ?? []).filter((i) => !s || i.label.toLocaleLowerCase('tr-TR').includes(s)).slice(0, 300);
  }, [items.data, q]);
  return (
    <PO.Root open={open} onOpenChange={setOpen}>
      <PO.Trigger disabled={disabled} className="inp flex items-center justify-between text-left disabled:opacity-70">
        <span className="truncate">{current?.label ?? (value != null ? String(value) : '')}</span>
        <ChevronDown size={13} className="shrink-0 muted" />
      </PO.Trigger>
      <PO.Portal>
        <PO.Content align="start" sideOffset={2} className="panel z-50 w-[min(520px,90vw)] rounded p-1 shadow-lg">
          <input autoFocus className="inp mb-1" placeholder="Ara…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="max-h-72 overflow-auto scroll-thin">
            <button className="block w-full rounded px-2 py-1 text-left muted hover:bg-[var(--grid-header)]" onClick={() => { onChange(null); setOpen(false); }}>(boş)</button>
            {items.isLoading && <div className="p-2 muted">Yükleniyor…</div>}
            {filtered.map((i) => (
              <button key={String(i.value)} className={cn('block w-full truncate rounded px-2 py-1 text-left hover:bg-[var(--grid-header)]', String(i.value) === String(value) && 'bg-[var(--grid-select)]')}
                onClick={() => { onChange(i.value); setOpen(false); }}>{i.label}</button>
            ))}
            {(items.data?.items.length ?? 0) > 300 && filtered.length === 300 && <div className="p-2 text-xs muted">İlk 300 kayıt gösteriliyor, aramayı daraltın.</div>}
          </div>
        </PO.Content>
      </PO.Portal>
    </PO.Root>
  );
}

function Details({ screen, id, details }: { screen: ScreenDef; id: number; details: FormData['details'] }) {
  const [active, setActive] = useState(String(details[0].index));
  const grid = (d: FormData['details'][number]) => (
    <DataGrid key={d.index}
      queryKey={['kayit-detay', screen.tag, id, d.index]}
      fetch={(q) => api(`/screens/${screen.tag}/kayit/${id}/detay/${d.index}/list`, { body: { query: q } })}
      buildColumns={(meta) => {
        const byName = new Map(meta.map((m) => [m.name.toLocaleUpperCase('tr-TR'), m]));
        const cols = d.columns.filter((c) => byName.has(c.alan.toLocaleUpperCase('tr-TR'))).map((c) => {
          const m = byName.get(c.alan.toLocaleUpperCase('tr-TR'))!;
          return { key: m.name, caption: c.baslik ?? m.name, sqlType: m.sqlType, hidden: c.gorunur === false, readonly: m.identity, align: NUM.has(m.sqlType) ? 'right' as const : undefined,
            editor: m.sqlType === 'bit' ? 'check' as const : NUM.has(m.sqlType) ? 'number' as const : DATE.has(m.sqlType) ? 'date' as const : 'text' as const };
        });
        for (const m of meta) if (!d.columns.some((c) => c.alan.toLocaleUpperCase('tr-TR') === m.name.toLocaleUpperCase('tr-TR'))) cols.push({ key: m.name, caption: m.name, sqlType: m.sqlType, hidden: true, readonly: true, align: undefined, editor: 'text' });
        return cols;
      }}
      editing={d.table ? {
        canInsert: screen.rights.insert, canUpdate: screen.rights.update, canDelete: screen.rights.delete,
        onSave: (row, original) => api(`/screens/${screen.tag}/kayit/${id}/detay/${d.index}/rows`, { body: { row, key: original ? { ID: original.ID } : undefined } }),
        onDelete: (row) => api(`/screens/${screen.tag}/kayit/${id}/detay/${d.index}/delete`, { body: { key: { ID: row.ID } } }),
      } : undefined}
      layoutKey={`kf:${screen.tag}:${d.index}`}
    />
  );
  return (
    <div className="flex min-h-[220px] flex-1 flex-col">
      {details.length > 1 && (
        <div className="flex gap-0.5">
          {details.map((d) => (
            <button key={d.index} className={cn('rounded-t px-3 py-1 text-[12.5px]', String(d.index) === active ? 'border b-border border-b-0 bg-[var(--panel)] font-medium' : 'muted')} onClick={() => setActive(String(d.index))}>{d.title}</button>
          ))}
        </div>
      )}
      {grid(details.find((d) => String(d.index) === active) ?? details[0])}
    </div>
  );
}

export { formatDate, parseDateInput };

/** Delphi'de başlığı verilmemiş alanlar alan adıyla görünür; web'de okunur hale getirilir (PLAKA_NO → Plaka No) */
function humanize(caption: string, field: string) {
  if (caption !== field) return caption;
  return field.toLocaleLowerCase('tr-TR').split('_').map((w) => w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1)).join(' ');
}
